import React from 'react';
import { Moon, Sun } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';

/**
 * The theme switch — one icon button, used in the navbar and the workspace.
 *
 * It shows the theme you would switch TO (a sun while dark), which is the
 * convention people read, and says so in its name: an icon alone was
 * announced as just "button" elsewhere on this site.
 */
const SIZES = {
  md: { box: 'h-10 w-10', icon: 'h-[18px] w-[18px]' },
  // The workspace's top bar, where every control is 28–32px tall.
  sm: { box: 'h-8 w-8', icon: 'h-4 w-4' },
};

export default function ThemeToggle({ size = 'md', className = '' }) {
  const { isDark, toggleTheme } = useTheme();
  const label = isDark ? 'Switch to light mode' : 'Switch to dark mode';
  const Icon = isDark ? Sun : Moon;
  const s = SIZES[size] || SIZES.md;

  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={label}
      title={label}
      className={`inline-flex ${s.box} shrink-0 items-center justify-center rounded-full border border-zinc-800 bg-zinc-900 text-zinc-300 transition-colors hover:bg-zinc-800 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/60 ${className}`}
    >
      <Icon className={s.icon} aria-hidden="true" />
    </button>
  );
}

const CHOICES = [
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'light', label: 'Light', icon: Sun },
];

/**
 * Both themes side by side, for settings. Two pressed-state buttons rather
 * than a radio group: each is one tab stop and one press, with nothing to
 * learn about arrow keys.
 */
export function ThemeChoice({ className = '' }) {
  const { theme, setTheme } = useTheme();

  return (
    <div role="group" aria-label="Theme" className={`inline-flex rounded-full border border-zinc-800 bg-zinc-950 p-1 ${className}`}>
      {CHOICES.map(({ value, label, icon: Icon }) => {
        const active = theme === value;
        return (
          <button
            key={value}
            type="button"
            aria-pressed={active}
            onClick={() => setTheme(value)}
            className={`flex h-9 items-center gap-2 rounded-full px-4 text-sm font-semibold transition-colors ${
              active ? 'bg-white text-zinc-950' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
            {label}
          </button>
        );
      })}
    </div>
  );
}
